<!-- eslint-disable vue/html-self-closing -->
<!-- eslint-disable vue/first-attribute-linebreak -->
<template>
    <div class="flex flex-col gap-1">
        <label class="hidden md:block text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Date Range
        </label>

        <!-- ── MOBILE: pill trigger ── -->
        <button type="button" class="md:hidden flex items-center gap-2.5 w-full h-[38px] px-3 rounded-lg text-sm text-left
             transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cs-main-300 focus:ring-offset-1"
            :class="hasRange
                ? 'bg-cs-main-50 border border-cs-main-300 text-cs-main-500'
                : 'bg-[#F9F9F9] border border-[#B1B1B4] text-gray-500 hover:border-cs-main-300'" aria-haspopup="dialog"
            @click="openSheet">
            <!-- Calendar icon -->
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 flex-shrink-0"
                :class="hasRange ? 'text-cs-main-400' : 'text-gray-400'" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
            </svg>

            <span class="flex-1 truncate font-medium">
                <span v-if="hasRange" class="text-cs-main-500">
                    {{ displayStart }} <span class="text-cs-main-300 mx-1">→</span> {{ displayEnd }}
                </span>
                <span v-else class="text-gray-400 font-normal">Select date range</span>
            </span>

            <!-- Clear badge (active state) -->
            <button v-if="hasRange" type="button" class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full
               bg-cs-main-100 hover:bg-cs-main-200 text-cs-main-400 transition focus:outline-none"
                aria-label="Clear date range" @click.stop="clearAndSubmit">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="3">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
            <!-- Chevron (empty state) -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9" />
            </svg>
        </button>

        <!-- ── DESKTOP: unified grouped date container ── -->
        <div class="hidden md:flex items-stretch h-[48px] rounded-lg overflow-hidden transition-all duration-200"
            :class="hasRange
                ? 'bg-cs-main-50 border border-cs-main-300'
                : 'bg-[#F9F9F9] border border-[#B1B1B4] focus-within:border-cs-main-300 focus-within:ring-2 focus-within:ring-cs-main-100'">

            <!-- Start date side -->
            <div class="relative flex items-center">
                <input type="date" v-model="localStart"
                    class="h-full font-normal text-sm bg-transparent border-none outline-none pl-2 pr-2 w-[135px] text-gray-700 placeholder-gray-400"
                    style="box-sizing:border-box;-webkit-appearance:none;" placeholder="Start date"
                    @change="syncToHidden" />
            </div>

            <!-- Divider arrow -->
            <div class="flex items-center px-1 text-gray-300 text-xs font-medium select-none">
                <span>→</span>
            </div>

            <!-- End date side -->
            <div class="relative flex items-center border-l border-[#E5E5E7]">
                <input type="date" v-model="localEnd"
                    class="h-full font-normal text-sm bg-transparent border-none outline-none pl-2 pr-2.5 w-[135px] text-gray-700 placeholder-gray-400"
                    style="box-sizing:border-box;-webkit-appearance:none;" placeholder="End date"
                    @change="syncToHidden" />
            </div>
        </div>

        <!-- ── Hidden inputs (always submitted with the form) ── -->
        <input type="hidden" :name="startName" :value="localStart" />
        <input type="hidden" :name="endName" :value="localEnd" />

        <!-- ── MOBILE: Bottom Sheet ── -->
        <teleport to="body">
            <div v-if="sheetVisible" role="dialog" aria-modal="true" aria-labelledby="drp-title"
                class="fixed inset-0 z-[9999] flex flex-col justify-end">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/50 transition-opacity duration-300"
                    :class="sheetOpen ? 'opacity-100' : 'opacity-0'" @click="closeSheet"></div>

                <!-- Panel -->
                <div class="relative bg-white rounded-t-3xl shadow-2xl w-full overflow-hidden transition-transform duration-300 ease-out"
                    :class="sheetOpen ? 'translate-y-0' : 'translate-y-full'">
                    <!-- Accent top strip -->
                    <div class="h-1 w-full bg-cs-main-500"></div>

                    <div class="px-5 pt-4 pb-6">
                        <!-- Handle bar -->
                        <div class="mx-auto w-10 h-1 bg-gray-200 rounded-full mb-4"></div>

                        <!-- Header -->
                        <div class="flex items-center justify-between mb-6">
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 rounded-full bg-cs-main-50 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-cs-main-400" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                </div>
                                <h2 id="drp-title" class="text-base font-bold text-gray-800">Filter by Date</h2>
                            </div>
                            <button type="button"
                                class="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition focus:outline-none"
                                aria-label="Close" @click="closeSheet">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" stroke-width="2.5">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <!-- Date card -->
                        <div class="flex flex-col rounded-2xl border border-gray-200 overflow-hidden bg-[#F9F9F9]">

                            <!-- Start date -->
                            <div class="flex flex-col gap-1 px-4 pt-4 pb-3">
                                <label for="drp-bs-start"
                                    class="text-[10px] font-bold text-cs-main-400 uppercase tracking-widest">Start
                                    Date</label>
                                <input type="date" id="drp-bs-start" v-model="sheetStart"
                                    class="w-full bg-transparent border-none outline-none text-base font-medium text-gray-800"
                                    style="box-sizing:border-box;-webkit-appearance:none;" />
                            </div>

                            <!-- Divider -->
                            <div class="flex items-center gap-2 px-4 py-2 border-t border-b border-gray-200 bg-white">
                                <div class="flex-1 h-px bg-gray-200"></div>
                                <span class="flex items-center gap-1 text-xs text-gray-400 font-medium">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 text-cs-main-300"
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                    to
                                </span>
                                <div class="flex-1 h-px bg-gray-200"></div>
                            </div>

                            <!-- End date -->
                            <div class="flex flex-col gap-1 px-4 pt-3 pb-4">
                                <label for="drp-bs-end"
                                    class="text-[10px] font-bold text-cs-main-400 uppercase tracking-widest">End
                                    Date</label>
                                <input type="date" id="drp-bs-end" v-model="sheetEnd"
                                    class="w-full bg-transparent border-none outline-none text-base font-medium text-gray-800"
                                    style="box-sizing:border-box;-webkit-appearance:none;" />
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="grid grid-cols-2 gap-3 mt-5">
                            <button type="button" class="button-cs-outline-primary-responsive" @click="clearSheet">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" stroke-width="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                                Clear
                            </button>
                            <button type="button" class="button-cs-primary-responsive" @click="applySheet">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" stroke-width="2">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function fmtIso(iso) {
    if (!iso) return ''
    const [y, m, d] = iso.split('-')
    return `${parseInt(d)} ${MONTHS[parseInt(m) - 1]} ${y}`
}

export default {
    name: 'DateRangePickerV3',

    props: {
        startName: { type: String, default: 'started_at' },
        endName: { type: String, default: 'ended_at' },
        initialStart: { type: String, default: '' },
        initialEnd: { type: String, default: '' },
        formId: { type: String, default: '' }
    },

    data() {
        return {
            localStart: this.initialStart,
            localEnd: this.initialEnd,
            sheetStart: '',
            sheetEnd: '',
            sheetVisible: false,
            sheetOpen: false
        }
    },

    computed: {
        hasRange() {
            return !!(this.localStart || this.localEnd)
        },
        displayStart() { return fmtIso(this.localStart) || 'Start' },
        displayEnd() { return fmtIso(this.localEnd) || 'End' }
    },

    mounted() {
        document.addEventListener('keydown', this.onKeydown)
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.onKeydown)
    },

    methods: {
        // ── Desktop ──────────────────────────────────────────────
        syncToHidden() {
            // v-model already keeps localStart/localEnd in sync;
            // hidden inputs re-render automatically.
        },

        // ── Mobile sheet ─────────────────────────────────────────
        openSheet() {
            this.sheetStart = this.localStart
            this.sheetEnd = this.localEnd
            this.sheetVisible = true
            document.body.style.overflow = 'hidden'
            this.$nextTick(() => { this.sheetOpen = true })
        },

        closeSheet() {
            this.sheetOpen = false
            document.body.style.overflow = ''
            setTimeout(() => { this.sheetVisible = false }, 300)
        },

        clearSheet() {
            this.sheetStart = ''
            this.sheetEnd = ''
        },

        applySheet() {
            this.localStart = this.sheetStart
            this.localEnd = this.sheetEnd
            this.closeSheet()
            this.$nextTick(() => { this.submitForm() })
        },

        clearAndSubmit() {
            this.localStart = ''
            this.localEnd = ''
            this.$nextTick(() => { this.submitForm() })
        },

        submitForm() {
            const form = this.formId
                ? document.getElementById(this.formId)
                : this.$el.closest('form')
            if (form) form.requestSubmit()
        },

        onKeydown(e) {
            if (e.key === 'Escape' && this.sheetVisible) this.closeSheet()
        }
    }
}
</script>
